// API client for DriveIT NQ

const API_URL = import.meta.env.PROD
  ? 'https://driveitnq-api.truthconsultingint.workers.dev'
  : 'http://localhost:8788';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  private getHeaders(includeAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  async get<T>(path: string, auth: boolean = true): Promise<ApiResponse<T>> {
    try {
      const res = await fetch(`${this.baseUrl}${path}`, {
        method: 'GET',
        headers: this.getHeaders(auth),
      });
      return await res.json();
    } catch (error) {
      console.error('API GET error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async post<T>(path: string, data: unknown, auth: boolean = true): Promise<ApiResponse<T>> {
    try {
      const res = await fetch(`${this.baseUrl}${path}`, {
        method: 'POST',
        headers: this.getHeaders(auth),
        body: JSON.stringify(data),
      });
      return await res.json();
    } catch (error) {
      console.error('API POST error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async patch<T>(path: string, data: unknown, auth: boolean = true): Promise<ApiResponse<T>> {
    try {
      const res = await fetch(`${this.baseUrl}${path}`, {
        method: 'PATCH',
        headers: this.getHeaders(auth),
        body: JSON.stringify(data),
      });
      return await res.json();
    } catch (error) {
      console.error('API PATCH error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async delete<T>(path: string, auth: boolean = true): Promise<ApiResponse<T>> {
    try {
      const res = await fetch(`${this.baseUrl}${path}`, {
        method: 'DELETE',
        headers: this.getHeaders(auth),
      });
      return await res.json();
    } catch (error) {
      console.error('API DELETE error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async upload(path: string, file: File, alt?: string): Promise<ApiResponse<{ id: string; url: string }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (alt) formData.append('alt', alt);

      const token = this.getToken();
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${this.baseUrl}${path}`, {
        method: 'POST',
        headers,
        body: formData,
      });
      return await res.json();
    } catch (error) {
      console.error('API upload error:', error);
      return { success: false, error: 'Upload failed' };
    }
  }

  getMediaUrl(mediaId: string): string {
    return `${this.baseUrl}/api/media/${mediaId}`;
  }
}

export const api = new ApiClient(API_URL);
export { API_URL };
